import {
  Box,
  Button,
  Checkbox,
  DatePicker,
  FormField,
  Input,
  Modal,
  Popover,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { Timestamp } from '@firebase/firestore';
import { Dispatch, FC, useState } from 'react';
import { Agp } from '../../api/agp';
import { createItem, deleteItem, Filter } from '../../api/generic';
import { adjustInventoryQuantity } from '../../api/inventory';
import { useAuthContext } from '../../contexts/AuthContext';
import useSubscribe from '../../hooks/useSubscribe';
import { RecipeContainer } from '../../models/Recipe';
import { getAgpState } from '../../utils/agp';
import { prefixCollection } from '../../utils/prefix';
import { capitalizeFirstLetter } from '../../utils/string';
import { toDate } from '../../utils/timestamp';

interface IAgpModalProps {
  visible: boolean;
  setVisible: Dispatch<boolean>;
  batchId: string;
  slug?: string;
  containers?: RecipeContainer[];
}

const AgpModal: FC<IAgpModalProps> = ({ visible, setVisible, batchId, slug, containers }) => {
  const { tenant, userObject } = useAuthContext();
  const [items, setItems] = useState<Agp[]>([]);
  const [error, setError] = useState<Error>();
  const [amounts, setAmounts] = useState<Record<string, number>>({});
  const [moveDate, setMoveDate] = useState(new Date().toISOString());
  const [note, setNote] = useState('');
  const [detractPerContainer, setDetractPerContainer] = useState(false);

  const sortAgpsByDate = (a: Agp, b: Agp) => {
    const dateA: Date = a.createdDate instanceof Timestamp ? toDate(a.createdDate) : (a.createdDate as Date);
    const dateB: Date = b.createdDate instanceof Timestamp ? toDate(b.createdDate) : (b.createdDate as Date);
    return dateA.getTime() - dateB.getTime();
  };

  const filter: Filter<Agp> = {
    field: 'batchId',
    operator: '==',
    value: batchId,
  };

  useSubscribe(prefixCollection('agp', tenant), setItems, setError, sortAgpsByDate, [filter]);

  const handleAddToAgp = async () => {
    if (!containers) return;
    for (const c of containers) {
      if (detractPerContainer) {
        await adjustInventoryQuantity(
          c.containerItem.id,
          {
            operation: 'remove',
            quantity: amounts[c.containerItem.id] ?? 0,
            action: 'inventory_depletion',
            userId: userObject?.uid,
          },
          tenant,
        );
      }

      const key = c.containerItem.id;
      const count = amounts[key] ?? 0;
      if (count > 0) {
        const size = c.sizeL ?? c.containerItem.capacityL ?? 0;
        const containerType = c.containerItem.name.toLowerCase().includes('can') ? 'can' : 'bottle';
        const name = `.${Math.round(size * 1000)}ml ${capitalizeFirstLetter(containerType)}`;
        await createItem<Agp>(prefixCollection('agp', tenant), {
          batchId,
          name,
          size,
          amount: count,
          createdDate: new Date(moveDate),
          note,
          createdBy: userObject?.uid,
          containerType,
          recipeSlug: slug,
        });
      }
    }
    setAmounts({});
    setNote('');
  };

  const handleDelete = async (id: string) => {
    await deleteItem(prefixCollection('agp', tenant), id.split('.')[0]);
  };

  return (
    <Modal
      header="AGP"
      visible={visible}
      onDismiss={() => {
        setVisible(false);
      }}
      size="large"
    >
      <SpaceBetween size="m">
        <Box variant="awsui-key-label">Add to AGP ({slug})</Box>
        <SpaceBetween size="m" direction="horizontal">
          {containers?.map((c) => (
            <FormField
              key={c.containerItem.id}
              label={
                <Popover
                  position="right"
                  size="large"
                  content={
                    <SpaceBetween size="m">
                      <Box variant="awsui-key-label">{c.containerItem.name}</Box>
                      {c.components?.length > 0 ? (
                        c.components?.map((comp) => (
                          <Box key={comp.item.id} variant="p" color="text-body-secondary">
                            {comp.item.name} ({comp.amountPerContainer} {comp.item.unit})
                          </Box>
                        ))
                      ) : (
                        <Box variant="p" color="text-body-secondary">
                          No components available
                        </Box>
                      )}
                    </SpaceBetween>
                  }
                >
                  <Box variant="awsui-key-label">{`Add ${c.containerItem.name} to AGP`}</Box>
                </Popover>
              }
            >
              <Input
                type="number"
                value={(amounts[c.containerItem.id] ?? 0).toString()}
                onChange={({ detail }) =>
                  setAmounts((prev) => ({ ...prev, [c.containerItem.id]: parseInt(detail.value) }))
                }
              />
            </FormField>
          ))}
          <FormField label="Date moved to AGP">
            <DatePicker value={moveDate} onChange={({ detail }) => setMoveDate(detail.value)} />
          </FormField>
          <FormField label="Note (optional)">
            <Input
              value={note}
              onChange={({ detail }) => setNote(detail.value)}
              placeholder="e.g. reserved for festival"
            />
          </FormField>
          <FormField
            label={
              <SpaceBetween size="s" direction="horizontal">
                <Checkbox
                  checked={detractPerContainer}
                  onChange={({ detail }) => setDetractPerContainer(detail.checked)}
                />
                <Box variant="awsui-key-label">Detract from inventory per container</Box>
              </SpaceBetween>
            }
          ></FormField>

          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
            }}
          >
            <Button disabled={!Object.values(amounts).some((v) => v > 0)} onClick={handleAddToAgp}>
              Add
            </Button>
          </div>
        </SpaceBetween>
        <hr />

        <Table<Agp>
          columnDefinitions={[
            {
              id: 'name',
              header: 'Name',
              cell: (item) => item.name,
            },
            {
              id: 'amount',
              header: 'Amount',
              cell: (item) => item.amount?.toString() ?? '—',
            },
            {
              id: 'size',
              header: 'Size (ml)',
              cell: (item) => item.size?.toString() ?? '—',
            },

            {
              id: 'date',
              header: 'Created',
              cell: (item) =>
                item.createdDate instanceof Date
                  ? item.createdDate.toLocaleDateString()
                  : (item.createdDate?.toDate().toLocaleDateString() ?? '—'),
            },
            {
              id: 'release',
              header: 'Released',
              cell: (item) =>
                item.releasedDate
                  ? item.releasedDate instanceof Date
                    ? item.releasedDate.toLocaleDateString()
                    : item.releasedDate.toDate().toLocaleDateString()
                  : '—',
            },
            {
              id: 'state',
              header: 'State',
              cell: (item) => capitalizeFirstLetter(getAgpState(item)),
            },

            {
              id: 'note',
              header: 'Note',
              cell: (item) => item.note ?? '—',
            },

            {
              id: 'action',
              header: '',
              cell: (item) => {
                const agpState = getAgpState(item);
                if (agpState === 'unreleased') {
                  return <Button variant="icon" iconName="close" onClick={() => handleDelete(item.id ?? '')} />;
                } else {
                  return null; // No delete button if released or sold
                }
              },
            },
          ]}
          items={items}
          trackBy="id"
        />
      </SpaceBetween>
    </Modal>
  );
};

export default AgpModal;
